import React, { useState, useMemo } from "react";
import moment from "moment";
import { useGetPilotConversationsQuery } from "apis/pilotConversation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import TableSkeleton from "components/ui/TableSkeleton";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import DatePicker from "components/financial/DatePicker";
import { formatDateTime } from "utils/helperFunctions";

const GRADES = [
  { value: "right", label: "Right", style: "bg-green-50 text-green-800" },
  { value: "wrong", label: "Wrong", style: "bg-red-50 text-red-800" },
  { value: "unsure", label: "Unsure", style: "bg-yellow-50 text-yellow-800" },
  {
    value: "waiting",
    label: "Not reviewed yet",
    style: "bg-lightergray text-darkergray",
  },
];

const CAUSES = [
  { value: "invented", label: "Invented feature" },
  { value: "wrongData", label: "Wrong data" },
  { value: "failedCall", label: "Failed call" },
  { value: "missedQuestion", label: "Missed the request" },
  { value: "other", label: "Other" },
];

const FLAGS = {
  failedCall: "A call failed",
  askedAgain: "Asked again",
};

// A place is "level:id", sent as that level's id.
const PLACE_LEVELS = {
  network: { label: "Network", param: "networkId" },
  group: { label: "Group", param: "groupId" },
  company: { label: "Company", param: "companyId" },
  office: { label: "Office", param: "officeId" },
  agent: { label: "Agent", param: "userId" },
};

const initialFilters = {
  grade: "",
  cause: "",
  fixed: "",
  place: "",
  startDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
};

const PAGE_SIZE = 25;

const gradeOf = (conversation) =>
  GRADES.find((g) => g.value === (conversation.review?.grade || "waiting"));

const causeLabel = (cause) =>
  CAUSES.find((c) => c.value === cause)?.label || "-";

const flagsOf = (conversation) =>
  (conversation.review?.flags || [])
    .map((flag) => FLAGS[flag] || flag)
    .join(", ");

// A wrong conversation whose cause has been fixed says so beside its grade, with what was changed and when.
const GradePill = ({ conversation }) => {
  const grade = gradeOf(conversation);
  const fixed = conversation.review?.fixed;
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${grade.style}`}
      >
        {grade.label}
      </span>
      {fixed?.at && (
        <span
          title={`${fixed.what} (${formatDateTime(fixed.at)})`}
          className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap bg-blue-50 text-blue-800"
        >
          Fixed
        </span>
      )}
    </span>
  );
};

const PilotConversations = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const queryParams = useMemo(() => {
    const { place, ...rest } = filters;
    const params = { page: currentPage, limit: PAGE_SIZE };
    Object.entries(rest).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    if (place) {
      const [level, id] = place.split(":");
      params[PLACE_LEVELS[level].param] = id;
    }
    return params;
  }, [filters, currentPage]);

  const {
    data: conversations,
    grades,
    causes,
    fixed,
    places,
    pagination,
    isPending,
    isFetching,
  } = useGetPilotConversationsQuery(queryParams);
  const placeOptions = places.map((place) => ({
    value: `${place.level}:${place._id}`,
    label: place.name,
    subLabel: [PLACE_LEVELS[place.level].label, place.detail].filter(Boolean).join(" · "),
  }));

  // Every change applies at once, from the first page.
  const changeFilters = (change) => {
    setFilters((prev) => ({ ...prev, ...change }));
    setCurrentPage(1);
  };
  // A tile picks its grade, or all of them again when it is picked already. A cause belongs to "wrong" alone.
  const pickGrade = (grade) =>
    changeFilters({ grade: filters.grade === grade ? "" : grade, cause: "", fixed: "" });
  // The Fixed tile lists only the conversations whose cause is fixed, or all again when it is picked already.
  const pickFixed = () =>
    changeFilters({ fixed: filters.fixed === "true" ? "" : "true", grade: "", cause: "" });
  const pickCause = (cause) =>
    changeFilters({ cause: filters.cause === cause ? "" : cause });
  const isReset = Object.keys(initialFilters).every(
    (key) => filters[key] === initialFilters[key],
  );
  const resetFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };
  // "Not reviewed yet" only shows while there is something waiting.
  const gradeTiles = GRADES.filter(
    (grade) =>
      grade.value !== "waiting" ||
      grades.waiting > 0 ||
      filters.grade === "waiting",
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Started",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">
            {formatDateTime(row.createdAt) ?? "-"}
          </span>
        ),
        isFilter: false,
        minWidth: "170px",
      },
      {
        accessorKey: "userId.name",
        header: "Agent",
        cell: (row) => (
          <span className="text-xs">{row.userId?.name || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "review.wanted",
        header: "What they wanted",
        cell: (row) => (
          <span className="line-clamp-2">
            {row.review?.wanted || row.turns?.[0]?.question || "-"}
          </span>
        ),
        isFilter: false,
        maxWidth: "380px",
      },
      {
        accessorKey: "turns",
        header: "Turns",
        cell: (row) => (
          <span className="text-xs">{row.turns?.length || 0}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "review.grade",
        header: "Grade",
        cell: (row) => <GradePill conversation={row} />,
        isFilter: false,
      },
      {
        accessorKey: "review.cause",
        header: "Cause",
        cell: (row) => (
          <span className="text-xs">{causeLabel(row.review?.cause)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "review.flags",
        header: "Flags",
        cell: (row) => <span className="text-xs">{flagsOf(row) || "-"}</span>,
        isFilter: false,
      },
    ],
    [],
  );

  // The first load and every change of filter or page show grey rows, not the old ones.
  const isLoading = isPending || isFetching;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold text-darkergray mb-2">
        Pilot Conversations
      </h1>
      <p className="text-sm text-darkgray mb-6">
        Each conversation an agent had with Pilot is checked within about 4
        hours, as a whole, against the calls it made and Beleef's docs. Greetings and small
        talk are left out. Agents see none of this.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <FilterField label="Period">
          <DatePicker
            value={{ startDate: filters.startDate, endDate: filters.endDate }}
            onChange={({ startDate, endDate }) => changeFilters({ startDate, endDate })}
          />
        </FilterField>
        <FilterField label="Network, group, company, office or agent">
          <div className="w-72">
            <SearchSelect
              options={placeOptions}
              value={placeOptions.find((option) => option.value === filters.place) || null}
              onChange={(option) => changeFilters({ place: option?.value || "" })}
              placeholder="Everyone"
              isClearable
            />
          </div>
        </FilterField>
        <Button
          className="gray-button disabled:cursor-not-allowed disabled:opacity-50"
          onClick={resetFilters}
          disabled={isReset}
        >
          Reset
        </Button>
      </div>

      <div
        className={`grid grid-cols-2 gap-3 ${filters.grade === "wrong" ? "mb-3" : "mb-6"} ${gradeTiles.length > 3 ? "md:grid-cols-5" : "md:grid-cols-4"}`}
      >
        {gradeTiles.map((grade) => (
          <StatCard
            key={grade.value}
            label={grade.label}
            value={isLoading ? <Skeleton width={32} height={24} /> : grades[grade.value] || 0}
            color={grade.value === "wrong" ? "text-red-700" : undefined}
            isSelected={filters.grade === grade.value}
            onClick={() => pickGrade(grade.value)}
          />
        ))}
        <StatCard
          label="Fixed"
          value={isLoading ? <Skeleton width={32} height={24} /> : fixed}
          color="text-blue-700"
          isSelected={filters.fixed === "true"}
          onClick={pickFixed}
        />
      </div>

      {filters.grade === "wrong" && (
        <div className="mb-6 rounded-lg border border-red-100 bg-red-50/40 p-3">
          <p className="mb-2 text-xs uppercase tracking-wide text-darkgray">
            Why it was wrong
          </p>
          <div className="flex flex-wrap gap-2">
            {CAUSES.map((cause) => (
              <button
                key={cause.value}
                type="button"
                onClick={() => pickCause(cause.value)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  filters.cause === cause.value
                    ? "border-red-700 bg-red-700 text-white"
                    : "border-mediumgray bg-white text-darkergray hover:border-red-300"
                }`}
              >
                {cause.label}
                <span className="font-semibold">
                  {isLoading ? <Skeleton width={14} /> : causes[cause.value] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <>
          <h4>Conversations</h4>
          <TableSkeleton rows={8} columns={columns.length} />
        </>
      ) : (
        <Table
          title="Conversations"
          columns={columns}
          data={conversations || []}
          pageSize={PAGE_SIZE}
          searchable={false}
          serverPagination={
            pagination
              ? {
                  page: pagination.page,
                  totalPages: pagination.totalPages,
                  total: pagination.total,
                  limit: pagination.limit,
                }
              : null
          }
          onPageChange={setCurrentPage}
          onRowClick={(row) => setSelected(row)}
          emptyText="No conversations in these dates."
        />
      )}

      <ConversationModal
        conversation={selected}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

const FilterField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-darkergray mb-1">
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  color = "text-darkergray",
  isSelected,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isSelected}
    className={`bg-white border rounded-lg p-3 text-center transition-colors ${
      isSelected ? "border-black ring-1 ring-black" : "border-mediumgray hover:border-darkgray"
    }`}
  >
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-darkgray uppercase tracking-wide">{label}</div>
  </button>
);

const Section = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-darkgray">
      {label}
    </span>
    {children}
  </div>
);

// A call keeps only the start of what came back (often cut off mid-way), so the parts worth reading are picked out of
// its text rather than parsed whole.
const STRING = '"((?:[^"\\\\]|\\\\.)*)"';
const unescaped = (value) => {
  try {
    return JSON.parse(`"${value}"`);
  } catch {
    return value;
  }
};
const textsOf = (text, key) =>
  [...String(text || "").matchAll(new RegExp(`"${key}":${STRING}`, "g"))].map(
    ([, value]) => unescaped(value),
  );
const textOf = (text, key) => textsOf(text, key)[0];
// The strings in a list field, such as "choices":["Yes","No"].
const stringsIn = (text, key) => {
  const list = String(text || "").split(`"${key}":[`)[1]?.split("]")[0] || "";
  return [...list.matchAll(new RegExp(STRING, "g"))].map(([, value]) =>
    unescaped(value),
  );
};
const listed = (items, most = 4) => {
  const unique = [...new Set(items.filter(Boolean))];
  return unique.length > most
    ? `${unique.slice(0, most).join(" · ")} and ${unique.length - most} more`
    : unique.join(" · ");
};
// getAgentPipeline → "Get agent pipeline".
const inWords = (name = "") => {
  const words = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const problemOf = (excerpt) => {
  const notCalled = textOf(excerpt, "notCalled");
  return (
    textOf(excerpt, "message") ||
    textOf(excerpt, "error") ||
    stringsIn(excerpt, "problems")[0] ||
    notCalled ||
    "It didn't work."
  );
};

// Why Pilot's own checks sent an answer back, in plain words.
const heldBack = (sentBack) =>
  /^Look first/.test(sentBack)
    ? "it hadn't checked Beleef yet, so it looked first"
    : /^Staying quiet/.test(sentBack)
      ? "it had to answer here, so it did"
      : /^Show your answer after/.test(sentBack)
        ? "it hadn't read its results yet, so it waited for them"
        : sentBack;

// What Pilot did, and what came of it, in a sentence each.
const describeCall = ({ tool, of, failed, excerpt }) => {
  const outcome = (worked) => (failed ? problemOf(excerpt) : worked);
  switch (tool) {
    case "callApi":
      return {
        did: inWords(of),
        came: outcome(
          excerpt?.includes('"needsConfirmation":true')
            ? "Prepared it for the agent to confirm"
            : "Worked",
        ),
      };
    case "identify": {
      const found = textsOf(excerpt, "label");
      return {
        did: `Looked for "${of}"`,
        came: outcome(found.length ? `Found ${listed(found)}` : "Found nothing"),
      };
    }
    case "browseApi":
      return {
        did: "Checked what it can do",
        came: outcome(`In ${listed(textsOf(excerpt, "area")) || "Beleef"}`),
      };
    case "howItWorks":
      return { did: `Read the guide on ${of}`, came: outcome("") };
    case "findScreens": {
      const screens = textsOf(excerpt, "name");
      return {
        did: "Looked for a screen",
        came: outcome(screens.length ? `Found ${listed(screens)}` : "Found none"),
      };
    }
    case "show":
    case "noReply": {
      const sentBack = textOf(excerpt, "notShown");
      return {
        did: tool === "show" ? "Tried to answer" : "Tried to stay quiet",
        came: sentBack ? `Held back: ${heldBack(sentBack)}` : "Answered",
      };
    }
    default:
      return { did: `${inWords(tool)}${of ? `: ${of}` : ""}`, came: outcome("") };
  }
};

// What an older turn showed beside its words (choices, records, links…), from before its calls were kept.
const SHOWN = {
  choices: (part) => `Choices offered: ${stringsIn(part, "choices").join(" · ")}`,
  records: (part) => `About: ${listed(textsOf(part, "name"))}`,
  links: (part) => `Links: ${listed(textsOf(part, "label"))}`,
  visual: (part) => `Showed: ${textOf(part, "title") || "a card"}`,
  starters: (part) =>
    `Started ${inWords(textOf(part, "starter") || "a flow").toLowerCase()} for ${textOf(part, "address") || "a property"}`,
  view: () => "Showed a list on screen",
  form: (part) => `Form: ${textOf(part, "summary") || "to fill in"}`,
  confirm: (part) => `Change waiting for a yes: ${textOf(part, "title") || ""}`,
  receipt: (part) =>
    `Change made: ${textOf(part, "title") || ""}${textOf(part, "message") ? ` (${textOf(part, "message")})` : ""}`,
  upload: () => "Asked for a file",
};
// A form's fields start the same way ({"name":"subject"…}), so only what the answer shows counts.
const shownWith = (excerpt) => [
  ...new Set(
    String(excerpt || "")
      .split('{"name":"')
      .slice(1)
      .map((part) => SHOWN[part.split('"')[0]]?.(part))
      .filter(Boolean),
  ),
];

// JSON indented for reading, also when it was cut off mid-way, as most excerpts are.
const indented = (text) => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    const pad = (depth) => `\n${"  ".repeat(depth)}`;
    let out = "";
    let depth = 0;
    let inString = false;
    for (let at = 0; at < text.length; at += 1) {
      const char = text[at];
      if (inString) {
        out += char;
        if (char === "\\") {
          out += text[at + 1] ?? "";
          at += 1;
        } else if (char === '"') inString = false;
      } else if (char === '"') {
        inString = true;
        out += char;
      } else if ((char === "{" || char === "[") && "}]".includes(text[at + 1])) {
        out += char + text[at + 1];
        at += 1;
      } else if (char === "{" || char === "[") {
        depth += 1;
        out += char + pad(depth);
      } else if (char === "}" || char === "]") {
        depth = Math.max(0, depth - 1);
        out += pad(depth) + char;
      } else if (char === ",") out += `,${pad(depth)}`;
      else if (char === ":") out += ": ";
      else out += char;
    }
    return `${out}\n… (cut off here)`;
  }
};

const TechnicalDetails = ({ text }) =>
  text ? (
    <details className="mt-1">
      <summary className="cursor-pointer text-xs text-darkgray">
        Technical details
      </summary>
      <pre className="mt-1 max-h-80 overflow-auto rounded bg-lightergray p-2 font-mono text-xs text-darkgray whitespace-pre-wrap break-words">
        {indented(text)}
      </pre>
    </details>
  ) : null;

const Calls = ({ calls }) =>
  calls?.length ? (
    <ul className="space-y-1">
      {calls.map((call, index) =>
        call.tool === "notRecorded" ? (
          <li key={index} className="border border-mediumgray rounded p-2 text-xs">
            <p className="text-darkgray">
              What it looked up wasn't saved for this older turn.
              {shownWith(call.excerpt).length > 0 && " What it showed with its answer:"}
            </p>
            {shownWith(call.excerpt).length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-darkergray">
                {shownWith(call.excerpt).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            <TechnicalDetails text={call.excerpt} />
          </li>
        ) : (
          <li key={index} className="border border-mediumgray rounded p-2 text-xs">
            {(() => {
              const { did, came } = describeCall(call);
              return (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-medium text-darkergray">{did}</span>
                  {call.failed ? (
                    <>
                      <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-800">
                        Failed
                      </span>
                      <span className="text-red-800">{came}</span>
                    </>
                  ) : (
                    came && <span className="text-darkgray">→ {came}</span>
                  )}
                </div>
              );
            })()}
            <TechnicalDetails text={call.excerpt} />
          </li>
        ),
      )}
    </ul>
  ) : (
    <span className="text-xs text-darkgray">
      No calls: it answered without reading anything.
    </span>
  );

const ConversationModal = ({ conversation, isOpen, onClose }) => {
  if (!conversation) return null;
  const { review = {} } = conversation;
  const totalUsd = (conversation.turns || []).reduce(
    (sum, turn) => sum + (turn.usd || 0),
    0,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Pilot Conversation"
      className="max-w-3xl"
    >
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3 text-xs text-darkgray">
          <GradePill conversation={conversation} />
          <span>{conversation.userId?.name || "-"}</span>
          <span>{formatDateTime(conversation.createdAt)}</span>
          <span>{conversation.turns?.length || 0} turns</span>
          <span>US${totalUsd.toFixed(3)}</span>
        </div>

        {review.reviewedAt && (
          <div className="border border-mediumgray rounded-lg p-3 space-y-3">
            {review.wanted && (
              <Section label="What they wanted">
                <span className="text-darkergray">{review.wanted}</span>
              </Section>
            )}
            {review.cause && (
              <Section label="Cause">
                <span className="text-darkergray">
                  {causeLabel(review.cause)}
                  {review.wrongTurn ? ` (turn ${review.wrongTurn})` : ""}
                </span>
              </Section>
            )}
            {review.why && (
              <Section label="Why">
                <span className="text-darkergray">{review.why}</span>
              </Section>
            )}
            {review.fix && (
              <Section label="Suggested fix">
                <span className="text-darkergray">{review.fix}</span>
              </Section>
            )}
            {review.fixed?.at && (
              <Section label="Fixed">
                <span className="text-darkergray">
                  {review.fixed.what} ({formatDateTime(review.fixed.at)})
                </span>
              </Section>
            )}
            {!!review.flags?.length && (
              <Section label="Flags">
                <span className="text-darkergray">{flagsOf(conversation)}</span>
              </Section>
            )}
          </div>
        )}

        {(conversation.turns || []).map((turn, index) => (
          <div
            key={index}
            className={`border rounded-lg p-3 space-y-3 ${
              review.wrongTurn === index + 1
                ? "border-red-300 bg-red-50"
                : "border-mediumgray"
            }`}
          >
            <div className="text-xs text-darkgray">
              Turn {index + 1} · {formatDateTime(turn.at)}
            </div>
            <Section label="Agent">
              <p className="text-darkergray whitespace-pre-wrap break-words">
                {turn.question}
              </p>
            </Section>
            <Section label="Pilot">
              <p className="text-darkergray whitespace-pre-wrap break-words">
                {turn.answer}
              </p>
            </Section>
            <Section label={`Calls (${turn.calls?.length || 0})`}>
              <Calls calls={turn.calls} />
            </Section>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default PilotConversations;
